import React, { useState } from 'react';
import { useSettings } from '../SettingsContext';

interface BillingDiscountModalProps {
  onClose: () => void;
}

const BillingDiscountModal: React.FC<BillingDiscountModalProps> = ({ onClose }) => {
  const { settings, updateSettings } = useSettings();
  const [quarterlyDiscount, setQuarterlyDiscount] = useState<number>(
    settings.checkoutConfig?.quarterlyDiscountPercentage ?? 0
  );
  const [yearlyDiscount, setYearlyDiscount] = useState<number>(
    settings.checkoutConfig?.yearlyDiscountPercentage ?? 0
  );
  const [saved, setSaved] = useState(false);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettings({
      checkoutConfig: {
        ...settings.checkoutConfig,
        quarterlyDiscountPercentage: Math.max(0, Math.min(100, Number(quarterlyDiscount) || 0)),
        yearlyDiscountPercentage: Math.max(0, Math.min(100, Number(yearlyDiscount) || 0)),
      }
    });
    setSaved(true);
    setTimeout(() => {
      onClose();
    }, 400);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95">
        <div className="p-6 md:p-8 border-b border-slate-100 flex justify-between items-center bg-white">
          <div>
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-700">
              Pricing Strategy
            </span>
            <h2 className="text-xl font-black text-slate-900 mt-1">Billing Cycle Discounts</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 rounded-xl hover:bg-slate-100">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 md:p-8 space-y-6">
          <p className="text-xs text-slate-500 font-medium leading-relaxed">
            Customize the discount percentage automatically applied when customers choose quarterly or annual billing cycles. Set to <strong className="text-slate-700">0%</strong> to disable discounts.
          </p>

          <div className="space-y-4">
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-[11px] font-black text-slate-700 uppercase tracking-wider">
                  Quarterly Billing Discount
                </label>
                <span className="text-xs font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                  {quarterlyDiscount}% OFF
                </span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500"
                  value={quarterlyDiscount}
                  onChange={e => setQuarterlyDiscount(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                />
                <button
                  type="button"
                  onClick={() => setQuarterlyDiscount(0)}
                  className="px-3 py-2 text-xs font-bold text-slate-500 hover:text-red-600 bg-white border border-slate-200 rounded-xl whitespace-nowrap hover:bg-slate-50"
                >
                  0% (None)
                </button>
              </div>
              <div className="flex gap-2 pt-1">
                {[0, 5, 10, 15].map(pct => (
                  <button
                    key={`q-${pct}`}
                    type="button"
                    onClick={() => setQuarterlyDiscount(pct)}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all ${quarterlyDiscount === pct ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-[11px] font-black text-slate-700 uppercase tracking-wider">
                  Annual / Yearly Billing Discount
                </label>
                <span className="text-xs font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                  {yearlyDiscount}% OFF
                </span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500"
                  value={yearlyDiscount}
                  onChange={e => setYearlyDiscount(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                />
                <button
                  type="button"
                  onClick={() => setYearlyDiscount(0)}
                  className="px-3 py-2 text-xs font-bold text-slate-500 hover:text-red-600 bg-white border border-slate-200 rounded-xl whitespace-nowrap hover:bg-slate-50"
                >
                  0% (None)
                </button>
              </div>
              <div className="flex gap-2 pt-1">
                {[0, 10, 15, 20, 25].map(pct => (
                  <button
                    key={`y-${pct}`}
                    type="button"
                    onClick={() => setYearlyDiscount(pct)}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all ${yearlyDiscount === pct ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 shadow-md active:scale-95 transition-all"
            >
              {saved ? 'Saved!' : 'Save Discounts'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillingDiscountModal;
